import styles from "./CreateUser.module.css";
import { getPageFromName } from "../../route_utils";
import { useState, useContext, useEffect } from "react";
import AuthContext from "../../context/AuthContext/AuthContext";
import { TextField, MenuItem } from "@mui/material";
import api_endpoints from "../../api";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import { useNavigate } from "react-router-dom";
import FormController from "../../widgets/FormController/FormController";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

const CreateUser = () => {
  const authContext = useContext(AuthContext);
  const {
    setShowSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [OTPCode, setOTPCode] = useState("");
  const [locations, setLocations] = useState([]);
  const { t } = useCustomTranslation("Créer un utilisateur");

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const organizationId = authContext.authContext.accessToken.organization_id;
      const response = await api_endpoints.getLocations(organizationId, "", "");
      if (response.status === 200) {
        setLocations(response.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateUser = async (e, formData) => {
    e.preventDefault();

    setLoading(true);
    try {
      let response = await api_endpoints.createUser(
        formData.first_name,
        formData.last_name,
        formData.email,
        formData.organization_id,
        formData.location_id || undefined,
        formData.kiosk_code?.trim() ? formData.kiosk_code.trim() : undefined
      );
      if (response.status === 200) {
        setMessageSuccessAlert(response.data);
        setShowSuccessAlert(true);
        let otpResponse = await api_endpoints.createUserOTP(
          formData.email,
          true,
          false,
          false
        );
        if (otpResponse.status === 200) {
          setOTPCode(otpResponse.data.otp);
          setModalOpen(true);
        }
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    navigate(getPageFromName("Super utilisateurs").path);
  };

  return (
    <div>
      <Dialog
        sx={{ "& .MuiDialog-paper": { maxHeight: 435 } }}
        maxWidth="sm"
        open={modalOpen}
      >
        <DialogTitle>{t("create_user_otp_dialog_title")}</DialogTitle>
        <DialogContent>
          <div className="flex flex-1 flex-col space-y-4">
            <h1>{t("create_user_otp_dialog_message")}</h1>
            <strong className="w-full text-center text-7xl font-extrabold">
              {OTPCode}
            </strong>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            {t("create_user_otp_close_button")}
          </Button>
        </DialogActions>
      </Dialog>
      <FormController
        initialValues={{
          organization_id: authContext.authContext.accessToken.organization_id,
          first_name: "",
          last_name: "",
          email: "",
          location_id: "",
          kiosk_code: "",
        }}
      >
        {({ value, handleChange, getFormFields }) => (
          <>
            <div className={`${styles.details_container} mt-4`}>
              <form
                id="publish_campaign_form"
                onSubmit={(e) => handleCreateUser(e, getFormFields())}
              >
                <div className={styles.inputs_container}>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextField
                      fullWidth
                      placeholder={t("create_user_firstname")}
                      label={t("create_user_firstname")}
                      required
                      name="first_name"
                      value={value.first_name}
                      onChange={handleChange}
                    />
                    <TextField
                      placeholder={t("create_user_lastname")}
                      label={t("create_user_lastname")}
                      style={{ marginLeft: 30 }}
                      required
                      fullWidth
                      name="last_name"
                      value={value.last_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextField
                      placeholder="Courriel"
                      label={t("create_user_email")}
                      required
                      fullWidth
                      name="email"
                      value={value.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextField
                      select
                      fullWidth
                      name="location_id"
                      label="Emplacement"
                      value={value.location_id}
                      onChange={handleChange}
                      helperText="Obligatoire si un NIP kiosk est défini"
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location._id} value={location._id}>
                          {location.location_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextField
                      fullWidth
                      placeholder={t("create_user_kiosk_pin")}
                      label={t("create_user_kiosk_pin")}
                      helperText={t("create_user_kiosk_pin_guide") || "4 ou 5 chiffres pour les administrateurs clients"}
                      name="kiosk_code"
                      value={value.kiosk_code}
                      onChange={(e) => {
                        const sanitizedValue = e.target.value.replace(/\D/g, "").slice(0, 5);
                        handleChange({
                          target: {
                            name: "kiosk_code",
                            value: sanitizedValue,
                          },
                        });
                      }}
                      inputProps={{
                        maxLength: 5,
                        inputMode: "numeric",
                        pattern: "^[0-9]{4,5}$",
                      }}
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="flex justify-end space-x-4 mt-4">
              <CancelButton
                disabled={loading}
                label={t("create_user_cancel_button")}
              />
              <ButtonLoading
                variant="contained"
                type="submit"
                form="publish_campaign_form"
                loading={loading}
              >
                {t("create_user_create_button")}
              </ButtonLoading>
            </div>
          </>
        )}
      </FormController>
    </div>
  );
};

export default CreateUser;
